/**
 * Per-campaign-type editability config.
 *
 * Some form fields can be edited in `modify` mode for certain campaign types
 * but not others (e.g., Cashback / Buyback allow ASIN edits in modify; NCEMI
 * and Deal lock the ASIN list once the campaign is created and route those
 * changes through the suspended-products flow instead).
 *
 * Centralising these rules keeps section components dumb and gives a single
 * place to update when new campaign types (Coupons, IBD, etc.) are onboarded.
 *
 * Usage:
 *   const isEditable = isFieldModifiable(campaignType, 'asins');
 *
 * To add a new rule, add a `FieldKey` member and one entry per campaign type.
 * To onboard a new campaign type, add it to `CampaignType` and add a row.
 */

import { CampaignType } from 'src/@types/valueMatching/enums';

/** Fields whose `modify`-mode editability differs across campaign types. */
export type ModifiableFieldKey = 'asins';

const MODIFY_MODE_EDITABILITY: Record<CampaignType, Record<ModifiableFieldKey, boolean>> = {
    [CampaignType.NO_COST_EMI]: { asins: false },
    [CampaignType.CASHBACK]:    { asins: true },
    [CampaignType.BUYBACK]:     { asins: true },
    [CampaignType.DEAL]:        { asins: false },
    [CampaignType.INSTANT_BANK_DISCOUNT]: { asins: false },
};

/**
 * Returns whether `field` is editable in `modify` mode for `campaignType`.
 * Returns `false` defensively when `campaignType` is undefined (e.g., URL slug
 * not yet resolved) so the UI renders the safer read-only state.
 */
export function isFieldModifiable(
    campaignType: CampaignType | undefined,
    field: ModifiableFieldKey,
): boolean {
    if (!campaignType) return false;
    return MODIFY_MODE_EDITABILITY[campaignType][field];
}
